import { Component, Input, Output, EventEmitter } from '@angular/core';
import { CdkDragDrop, moveItemInArray, transferArrayItem } from '@angular/cdk/drag-drop';
import { FormField, FormLevel } from '../../utils/form-builder.utils';
import { FieldType, currentDraggedFieldType } from '../field-palette/field-palette.component';
import { generateFieldLabel } from '../../utils/form-builder.utils';

@Component({
  selector: 'lib-nested-level',
  templateUrl: './nested-level.component.html',
  styleUrls: ['./nested-level.component.scss'],
})
export class NestedLevelComponent {
  @Input() level!: FormLevel;
  @Input() parent!: FormLevel;
  @Input() depth = 1;
  @Input() selectedField: FormField | null = null;
  @Input() selectedLevelForConfig: FormLevel | null = null;
  @Input() connectedDropLists: string[] = [];

  @Output() fieldAdded = new EventEmitter<{ field: FormField; level: FormLevel }>();
  @Output() fieldRemoved = new EventEmitter<{ field: FormField; level: FormLevel }>();
  @Output() fieldsReordered = new EventEmitter<{ fields: FormField[]; level: FormLevel }>();
  @Output() fieldSelected = new EventEmitter<{ field: FormField; level: FormLevel }>();
  @Output() levelSelected = new EventEmitter<FormLevel>();
  @Output() addNestedLevel = new EventEmitter<FormLevel>();
  @Output() removeLevel = new EventEmitter<{ level: FormLevel; parent: FormLevel }>();
  @Output() duplicateLevel = new EventEmitter<{ level: FormLevel; parent: FormLevel }>();

  readonly maxDepth = 10; // Maximum nesting depth (up to 10 levels)
  isDragOver = false;

  toggleExpanded(): void {
    this.level.isExpanded = !this.level.isExpanded;
  }

  // TrackBy function for ngFor
  trackByFieldId(index: number, field: FormField): string {
    return field.id;
  }

  onDrop(event: CdkDragDrop<FormField[]>): void {
    if (event.previousContainer === event.container) {
      moveItemInArray(this.level.fields, event.previousIndex, event.currentIndex);
      this.fieldsReordered.emit({ fields: this.level.fields, level: this.level });
    } else {
      // Move field from another level
      transferArrayItem(
        event.previousContainer.data,
        event.container.data,
        event.previousIndex,
        event.currentIndex
      );
      this.fieldsReordered.emit({ fields: this.level.fields, level: this.level });
    }
  }

  // Native HTML5 drop - for palette items
  onNativeDragOver(event: DragEvent): void {
    if (!currentDraggedFieldType) return;
    event.preventDefault();
    this.isDragOver = true;
    if (event.dataTransfer) {
      event.dataTransfer.dropEffect = 'copy';
    }
  }

  onNativeDragLeave(event: DragEvent): void {
    this.isDragOver = false;
  }

  onNativeDrop(event: DragEvent): void {
    if (!currentDraggedFieldType) return;
    event.preventDefault();
    this.isDragOver = false;

    const fieldType = currentDraggedFieldType;
    const newField = this.createFieldFromType(fieldType);
    this.level.fields.push(newField);
    this.fieldAdded.emit({ field: newField, level: this.level });
  }

  createFieldFromType(fieldType: FieldType): FormField {
    const baseField: FormField = {
      id: 'field_' + Date.now() + '_' + Math.random().toString(36).substring(2, 9),
      type: fieldType.type,
      label: generateFieldLabel(fieldType.type, this.level.fields),
      required: false,
      validators: [],
    };

    switch (fieldType.type) {
      case 'text':
        baseField.hint = '';
        baseField.maxLength = 255;
        break;
      case 'textarea':
        baseField.hint = '';
        baseField.maxLength = 2000;
        break;
      case 'number':
        baseField.min = 0;
        baseField.max = 999999;
        break;
      case 'select':
        baseField.options = ['Option 1', 'Option 2', 'Option 3'];
        break;
      case 'photo':
        baseField.maxFiles = 5;
        // DOCU-893: siehe form-canvas.component.ts — der Default muss auf beiden
        // Einfügewegen (Root-Canvas und Unterebene) gesetzt werden, sonst hängt er
        // davon ab, wohin der Nutzer das Feld zieht.
        baseField.allowAnnotation = true;
        break;
    }

    return baseField;
  }

  selectField(field: FormField): void {
    this.fieldSelected.emit({ field, level: this.level });
  }

  removeField(field: FormField, event: Event): void {
    event.stopPropagation();
    this.fieldRemoved.emit({ field, level: this.level });
  }

  onAddNestedLevel(): void {
    this.addNestedLevel.emit(this.level);
  }

  onRemoveLevel(): void {
    this.removeLevel.emit({ level: this.level, parent: this.parent });
  }

  onDuplicateLevel(): void {
    this.duplicateLevel.emit({ level: this.level, parent: this.parent });
  }

  canAddNestedLevel(): boolean {
    return this.depth < this.maxDepth;
  }

  onSelectLevel(): void {
    this.levelSelected.emit(this.level);
  }

  isThisLevelSelected(): boolean {
    return this.selectedLevelForConfig?.id === this.level?.id;
  }

  isFieldSelected(field: FormField): boolean {
    return this.selectedField?.id === field.id;
  }

  getFieldTypeIcon(type: string): string {
    const iconMap: Record<string, string> = {
      text: 'text_fields',
      textarea: 'notes',
      number: 'pin',
      date: 'calendar_today',
      time: 'schedule',
      select: 'arrow_drop_down_circle',
      checkbox: 'check_box',
      photo: 'photo_camera',
    };
    return iconMap[type] || 'help_outline';
  }

  getDepthColor(): string {
    const colors = ['#1976d2', '#0288d1', '#26a69a', '#7e57c2', '#ff7043'];
    return colors[this.depth % colors.length];
  }

  // Forward events from child nested levels
  onChildFieldAdded(event: { field: FormField; level: FormLevel }): void {
    this.fieldAdded.emit(event);
  }

  onChildFieldRemoved(event: { field: FormField; level: FormLevel }): void {
    this.fieldRemoved.emit(event);
  }

  onChildFieldsReordered(event: { fields: FormField[]; level: FormLevel }): void {
    this.fieldsReordered.emit(event);
  }

  onChildFieldSelected(event: { field: FormField; level: FormLevel }): void {
    this.fieldSelected.emit(event);
  }

  onChildLevelSelected(level: FormLevel): void {
    this.levelSelected.emit(level);
  }

  onChildAddNestedLevel(parentLevel: FormLevel): void {
    this.addNestedLevel.emit(parentLevel);
  }

  onChildRemoveLevel(event: { level: FormLevel; parent: FormLevel }): void {
    const index = event.parent.children.findIndex((c) => c.id === event.level.id);
    if (index > -1) {
      event.parent.children.splice(index, 1);
    }
  }

  onChildDuplicateLevel(event: { level: FormLevel; parent: FormLevel }): void {
    this.duplicateLevel.emit(event);
  }
}
